// ExpressCheckout payment processor.
//
// Single orchestration point for all wallets and modes. Each wallet provides
// an adapter (see ./adapters/README) with the three wallet-specific
// extractions; this module picks the right payload builder based on
// getMode(config), submits via submitExpressCheckoutPayment, and returns a
// normalized result. Postmessaging is left to the caller so each wallet can
// map the result to the vendor SDK's status contract.

import {
  buildOneTimePayload,
  buildRecurringPayload,
  buildTokenizationPayload,
  getMode,
} from "./payload";
import { submitExpressCheckoutPayment } from "./api";

const PAYLOAD_BUILDERS = {
  one_time: buildOneTimePayload,
  autopay: buildRecurringPayload,
  tokenization: buildTokenizationPayload,
};

// A wallet submission succeeds only when the request was 2xx AND the Payabli
// envelope reports "Success". `data.pageIdentifier` set on the error envelope
// (e.g. a refreshed token) does not imply success.
export function isWalletPaymentSuccess({ ok, data } = {}) {
  return Boolean(ok) && data?.responseText === "Success";
}

// Builds a real Error from a failed submission. The backend returns its error
// envelope (responseCode + responseData.explanation) even on a 4xx status, so
// surface that reason as `error.message` — the partner-facing contract reads
// `error.message` — and keep the structured fields for callers that want them.
export function walletErrorFromResult({ data } = {}) {
  const message =
    data?.responseData?.explanation ||
    data?.responseData?.resultText ||
    data?.responseText ||
    "Payment authorization failed";
  const error = new Error(message);
  error.responseCode = data?.responseCode;
  error.responseData = data?.responseData;
  return error;
}

export async function processWalletPayment({
  adapter,
  config,
  paypoint,
  urlApi,
  token,
  event,
} = {}) {
  if (!adapter) {
    throw new Error("processWalletPayment: adapter is required");
  }

  const mode = getMode(config);
  const buildPayload = PAYLOAD_BUILDERS[mode];
  if (!buildPayload) {
    throw new Error(
      `processWalletPayment: unsupported mode '${mode}'. Must be one of: ${Object.keys(PAYLOAD_BUILDERS).join(', ')}`
    );
  }

  const walletToken = adapter.extractWalletToken(event);
  const cardHolder = adapter.extractCardHolder(event);
  const customerData = adapter.extractBillingData(config, event);

  const payload = buildPayload({
    config,
    walletType: adapter.walletType,
    walletToken,
    cardHolder,
    customerData,
  });

  const { ok, data } = await submitExpressCheckoutPayment({
    urlApi,
    token,
    config,
    payload,
  });

  return {
    ok,
    data,
    flow: mode,
    payload,
    walletType: adapter.walletType,
  };
}
